import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/api';
import './IndustryStandardsModal.css';

export default function IndustryStandardsModal({ isOpen, onClose, wellId = null, wellName = null }) {
  const [activeTab, setActiveTab] = useState('import');
  const [standardType, setStandardType] = useState('witsml');
  const [wells, setWells] = useState([]);
  const [selectedWellId, setSelectedWellId] = useState(wellId);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [importResults, setImportResults] = useState(null);
  const [validationResults, setValidationResults] = useState(null);

  // Export options
  const [includeWellbores, setIncludeWellbores] = useState(true);
  const [includeTrajectories, setIncludeTrajectories] = useState(true);
  const [includeLogs, setIncludeLogs] = useState(true);

  useEffect(() => {
    if (isOpen && activeTab === 'export') {
      fetchWells();
    }
    if (wellId) {
      setSelectedWellId(wellId);
    }
  }, [isOpen, activeTab, wellId]);

  const fetchWells = async () => {
    try {
      const endpoint = standardType === 'witsml' ? '/api/witsml/wells' : '/api/osdu/wells';
      const response = await apiFetch(endpoint);
      setWells(response.wells || []);
    } catch (error) {
      console.error('Error fetching wells:', error);
      setMessage('Failed to fetch wells for export');
      setMessageType('error');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setMessage('');
    setImportResults(null);
    setValidationResults(null);
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setMessage('Please select a file to validate');
      setMessageType('error');
      return;
    }

    if (standardType !== 'osdu') {
      setMessage('Validation is only available for OSDU files');
      setMessageType('error');
      return;
    }

    setIsProcessing(true);
    setMessage('');
    setValidationResults(null);

    try {
      const formData = new FormData();
      formData.append('osduFile', selectedFile);

      const response = await apiFetch('/api/osdu/validate', {
        method: 'POST',
        body: formData
      });

      setValidationResults(response);
      setMessage('File validation completed successfully');
      setMessageType('success');
    } catch (error) {
      console.error('Validation error:', error);
      setMessage(`Validation failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setMessage('Please select a file to import');
      setMessageType('error');
      return;
    }

    setIsProcessing(true);
    setMessage('');
    setImportResults(null);

    try {
      const formData = new FormData();
      const fileFieldName = standardType === 'witsml' ? 'witsmlFile' : 'osduFile';
      formData.append(fileFieldName, selectedFile);

      const endpoint = standardType === 'witsml' ? '/api/witsml/import' : '/api/osdu/import';
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: formData
      });

      setImportResults(response.results);
      setMessage(`${standardType.toUpperCase()} import completed successfully`);
      setMessageType('success');
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Import error:', error);
      setMessage(`Import failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExport = async () => {
    if (!selectedWellId) {
      setMessage('Please select a well to export');
      setMessageType('error');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const params = new URLSearchParams({
        wellId: selectedWellId,
        includeWellbores: includeWellbores.toString(),
        includeTrajectories: includeTrajectories.toString(),
        includeLogs: includeLogs.toString()
      });

      const endpoint = standardType === 'witsml' ? '/api/witsml/export' : '/api/osdu/export';
      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from response headers
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `well_${selectedWellId}_${standardType}.${standardType === 'witsml' ? 'xml' : 'json'}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage(`${standardType.toUpperCase()} export completed successfully`);
      setMessageType('success');

    } catch (error) {
      console.error('Export error:', error);
      setMessage(`Export failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setMessage('');
    setImportResults(null);
    setValidationResults(null);
    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';
  };

  const renderImportResults = () => {
    if (!importResults) return null;

    return (
      <div className="import-results">
        <h4>Import Results</h4>
        <div className="results-summary">
          <div className="result-item">
            <span className="result-label">Wells:</span>
            <span className="result-success">✓ {importResults.wells.imported}</span>
            <span className="result-error">✗ {importResults.wells.skipped}</span>
          </div>
          <div className="result-item">
            <span className="result-label">Wellbores:</span>
            <span className="result-success">✓ {importResults.wellbores.imported}</span>
            <span className="result-error">✗ {importResults.wellbores.skipped}</span>
          </div>
          <div className="result-item">
            <span className="result-label">Trajectories:</span>
            <span className="result-success">✓ {importResults.trajectories.imported}</span>
            <span className="result-error">✗ {importResults.trajectories.skipped}</span>
          </div>
          <div className="result-item">
            <span className="result-label">Logs:</span>
            <span className="result-success">✓ {importResults.logs.imported}</span>
            <span className="result-error">✗ {importResults.logs.skipped}</span>
          </div>
        </div>

        {/* Show errors if any */}
        {(importResults.wells.errors.length > 0 || 
          importResults.wellbores.errors.length > 0 || 
          importResults.trajectories.errors.length > 0 || 
          importResults.logs.errors.length > 0) && (
          <div className="import-errors">
            <h5>Errors:</h5>
            {importResults.wells.errors.map((error, idx) => (
              <div key={idx} className="error-item">
                <strong>Well:</strong> {error.wellName} - {error.error}
              </div>
            ))}
            {importResults.wellbores.errors.map((error, idx) => (
              <div key={idx} className="error-item">
                <strong>Wellbore:</strong> {error.wellboreName} - {error.error}
              </div>
            ))}
            {importResults.trajectories.errors.map((error, idx) => (
              <div key={idx} className="error-item">
                <strong>Trajectory:</strong> {error.trajectoryUID} - {error.error}
              </div>
            ))}
            {importResults.logs.errors.map((error, idx) => (
              <div key={idx} className="error-item">
                <strong>Log:</strong> {error.logName} - {error.error}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderValidationResults = () => {
    if (!validationResults) return null;

    return (
      <div className="validation-results">
        <h4>Validation Results</h4>
        <div className="validation-summary">
          <div className="summary-item">
            <span className="summary-label">Wells:</span>
            <span className="summary-value">{validationResults.summary.wells}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Wellbores:</span>
            <span className="summary-value">{validationResults.summary.wellbores}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Trajectories:</span>
            <span className="summary-value">{validationResults.summary.trajectories}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Logs:</span>
            <span className="summary-value">{validationResults.summary.logs}</span>
          </div>
        </div>

        {validationResults.preview && (
          <div className="validation-preview">
            <h5>Preview (first 3 records):</h5>
            {validationResults.preview.wells.length > 0 && (
              <div className="preview-section">
                <h6>Wells:</h6>
                {validationResults.preview.wells.map((well, idx) => (
                  <div key={idx} className="preview-item">
                    {well.WellName} ({well.API}) - {well.Field}
                  </div>
                ))}
              </div>
            )}
            {validationResults.preview.wellbores.length > 0 && (
              <div className="preview-section">
                <h6>Wellbores:</h6>
                {validationResults.preview.wellbores.map((wellbore, idx) => (
                  <div key={idx} className="preview-item">
                    {wellbore.WellboreName} - {wellbore.WellboreType}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="industry-standards-overlay">
      <div className="industry-standards-modal">
        <div className="modal-header">
          <h3>Industry Standards Integration</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Standard Type Selection */}
          <div className="standard-type-selection">
            <label>
              <input
                type="radio"
                value="witsml"
                checked={standardType === 'witsml'}
                onChange={(e) => {
                  setStandardType(e.target.value);
                  resetForm();
                }}
              />
              WITSML (XML)
            </label>
            <label>
              <input
                type="radio"
                value="osdu"
                checked={standardType === 'osdu'}
                onChange={(e) => {
                  setStandardType(e.target.value);
                  resetForm();
                }}
              />
              OSDU (JSON)
            </label>
          </div>

          {/* Tab Selection */}
          <div className="tab-navigation">
            <button
              className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('import');
                resetForm();
              }}
            >
              Import
            </button>
            <button
              className={`tab-btn ${activeTab === 'export' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('export');
                resetForm();
                if (activeTab !== 'export') {
                  fetchWells();
                }
              }}
            >
              Export
            </button>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`message ${messageType}`}>
              {message}
            </div>
          )}

          {/* Import Tab */}
          {activeTab === 'import' && (
            <div className="import-section">
              <div className="file-selection">
                <label htmlFor="file-input">
                  Select {standardType.toUpperCase()} file:
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept={standardType === 'witsml' ? '.xml' : '.json'}
                  onChange={handleFileSelect}
                />
                {selectedFile && (
                  <div className="selected-file">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>

              <div className="import-actions">
                {standardType === 'osdu' && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleValidate}
                    disabled={!selectedFile || isProcessing}
                  >
                    {isProcessing ? 'Validating...' : 'Validate File'}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={!selectedFile || isProcessing}
                >
                  {isProcessing ? 'Importing...' : `Import ${standardType.toUpperCase()}`}
                </button>
              </div>

              {renderValidationResults()}
              {renderImportResults()}
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="export-section">
              <div className="well-selection">
                <label htmlFor="well-select">Select Well:</label>
                <select
                  id="well-select"
                  value={selectedWellId || ''}
                  onChange={(e) => setSelectedWellId(e.target.value)}
                >
                  <option value="">Select a well...</option>
                  {wells.map(well => (
                    <option key={well.WellID} value={well.WellID}>
                      {well.WellName} ({well.API}) - {well.Field}
                    </option>
                  ))}
                </select>
                {wellName && selectedWellId === wellId && (
                  <div className="current-well">
                    Current well: {wellName}
                  </div>
                )}
              </div>

              <div className="export-options">
                <h4>Export Options:</h4>
                <label>
                  <input
                    type="checkbox"
                    checked={includeWellbores}
                    onChange={(e) => setIncludeWellbores(e.target.checked)}
                  />
                  Include Wellbores
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeTrajectories}
                    onChange={(e) => setIncludeTrajectories(e.target.checked)}
                  />
                  Include Trajectories
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeLogs}
                    onChange={(e) => setIncludeLogs(e.target.checked)}
                  />
                  Include Logs
                </label>
              </div>

              <div className="export-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleExport}
                  disabled={!selectedWellId || isProcessing}
                >
                  {isProcessing ? 'Exporting...' : `Export as ${standardType.toUpperCase()}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="format-info">
            <small>
              {standardType === 'witsml' ? (
                <>
                  <strong>WITSML:</strong> Wellsite Information Transfer Standard Markup Language (XML format)
                  <br />
                  Supports wells, wellbores, trajectories, and logs data exchange
                </>
              ) : (
                <>
                  <strong>OSDU:</strong> Open Subsurface Data Universe (JSON format)
                  <br />
                  Industry standard for energy data platform integration
                </>
              )}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
} 